import type { DirectiveBinding, ObjectDirective, VNode } from 'vue';
import Core from '@/core/core/core';

interface HTMLElementWithSwimUpOptions extends HTMLElement {
	directiveSwipUpOptions: {
		distance: number,
		percent: number,
	};
}

let appSwimUpInited = false;
let appSwimUpObserver: IntersectionObserver;
const appSwimUpEls = new Map();

/**
 * Отодвинуть блок в зависимости от скролла старинцы
 * @param el
 * @param windowHeight
 */
const swimUpElTransform = (el: HTMLElementWithSwimUpOptions, windowHeight: number): void => {
	const { distance, percent } = el.directiveSwipUpOptions;

	const elTop = el.getBoundingClientRect().top;

	// во столько раз triggerHeight должен быть меньше, чтобы закончить подплытие к нужной высоте экрана
	const triggerHeightReducer = windowHeight * percent / 100 / distance;
	const triggerHeight = (windowHeight - elTop) / triggerHeightReducer;

	if (triggerHeight <= distance) {
		el.style.transform = `translateY(${distance - triggerHeight}px)`;
	} else {
		el.style.transform = 'translateY(0px)';
	}
};

const swimUpInit = (_el: HTMLElementWithSwimUpOptions): void => {
	let windowHeight = window.innerHeight;

	window.addEventListener('scroll', () => {
		appSwimUpEls.forEach((el) => swimUpElTransform(el, windowHeight));
	}, { passive: true });

	// проверка того, что элемент в зоне видимости
	appSwimUpObserver = new IntersectionObserver(entries => {
		windowHeight = window.innerHeight;

		entries.forEach((entry) => {
			const el = entry.target as HTMLElementWithSwimUpOptions;

			if (entry.intersectionRatio < 0.1) {
				// элемент за областью видимости
				appSwimUpEls.delete(el);
			} else {
				// элемент на экране
				appSwimUpEls.set(el, el);
			}

			if (entry.intersectionRatio === 0) {
				const { distance } = el.directiveSwipUpOptions;
				el.style.transform = `translateY(${distance}px)`;
			}
		});
	}, {
		threshold: 0.1,
	});
};

/**
 * Добавление анимации подплытия вверх для блока
 */
const swimUp = {
	mounted(el: HTMLElementWithSwimUpOptions, binding: DirectiveBinding, _vnode: VNode): void {
		if (Core.state.isMobileUA) return;

		el.directiveSwipUpOptions = {
			distance: binding.value?.distance ?? 100, // количество px на которые блок изначально смещен вниз,
			percent: binding.value?.percent ?? 30, // процент высоты экрана поднявшись на который блок закончит подплытие
		};

		if (!appSwimUpInited) {
			swimUpInit(el);

			appSwimUpInited = true;
		}

		appSwimUpObserver.observe(el);
	},

	unmounted(el: HTMLElementWithSwimUpOptions, _binding: DirectiveBinding, _vnode: VNode) {
		appSwimUpEls.delete(el);
		appSwimUpObserver?.unobserve(el);
	},
};

export default swimUp;
